// custom-toast.component.ts
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { ErrorResponse } from 'src/app/_models/ErrorResponse.model';

@Component({
  standalone: false,
	selector: 'app-custom-toast',
	template: `
		<p-toast key="error-toast" position="top-right" [baseZIndex]="5000">
		  <ng-template let-message #message>
		    <div class="flex flex-column align-items-start" style="flex: 1">
		      <div class="flex align-items-center">
		        <i class="pi pi-exclamation-triangle mr-2" style="font-size: 1.5rem"></i>
		        <div class="ml-2">
		          <div class="p-toast-summary">{{ message.summary }}</div>
		          <div class="p-toast-detail">{{ message.detail }}</div>
		        </div>
		      </div>
		      @if (message.data) {
		        <div class="mt-3">
		          <button pButton type="button" class="p-button-sm p-button-outlined p-button-secondary" (click)="showErrorDetails(message.data)"><i class="p-button-icon pi pi-info-circle"></i><span class="p-button-label">Visualizza Dettagli</span></button>
		        </div>
		      }
		    </div>
		  </ng-template>
		</p-toast>
		
		<p-toast key="error-details" position="center" [baseZIndex]="6000" [breakpoints]="{ '1920px': { width: '800px', top: '25%', left: 'calc(50% - 400px)', transform: 'none' } }">
		  <ng-template let-message #message>
		    <div class="flex flex-column" style="flex: 1; max-width: 800px;">
		      <div class="flex align-items-center mb-3">
		        <i class="pi pi-info-circle mr-2" style="font-size: 1.5rem"></i>
		        <div class="ml-2">
		          <div class="font-medium text-900">{{ message.summary }}</div>
		        </div>
		      </div>
		      <div class="mb-3">
		        <pre class="text-sm bg-gray-100 p-3 border-round overflow-auto" style="max-height: 300px; white-space: pre-wrap;">{{ message.detail }}</pre>
		      </div>
		      <div>
		        <button pButton type="button" class="p-button-sm p-button-outlined p-button-secondary" (click)="sendErrorViaEmail(message.detail)"><i class="p-button-icon pi pi-envelope"></i><span class="p-button-label">Invia via Email</span></button>
		        <button pButton type="button" class="p-button-sm p-button-outlined p-button-secondary" (click)="copyErrorDetails(message.detail)"><i class="p-button-icon pi pi-copy"></i><span class="p-button-label">Copia Dettagli</span></button>
		      </div>
		    </div>
		  </ng-template>
		</p-toast>
		
		<!-- Regular toast for non-admin users and other messages -->
		<p-toast position="top-right" [baseZIndex]="5000"></p-toast>
		`,
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: [
		`
			:host ::ng-deep .p-toast .p-toast-message {
				margin: 0 0 1rem 0;
			}

			:host ::ng-deep .p-toast .p-toast-message .p-toast-message-content {
				padding: 1rem;
			}

			pre {
				font-family: 'Courier New', monospace;
				font-size: 0.875rem;
				line-height: 1.4;
			}
		`,
	],
})
export class CustomToastComponent {
	constructor(private messageService: MessageService) {}

	showErrorDetails(errorData: ErrorResponse) {
		// This will be called when admin clicks the detail button
		console.log('[showErrorDetails] errorData ricevuto:', errorData);
		const detailMessage = this.formatErrorDetails(errorData);
		console.log('[showErrorDetails] detailMessage formattato:', detailMessage);

		this.messageService.add({
			severity: 'info',
			summary: 'Riepilogo Errore',
			detail: detailMessage,
			sticky: true,
			key: 'error-details',
		});
	}

	sendErrorViaEmail(errorData: any) {
		const emailSubject = encodeURIComponent('Rapporto Errore');
		let emailBody = errorData;

		if (emailBody.length > 1200) {
			emailBody = emailBody.substring(0, 1200) + '\n\n[Troncato a causa della lunghezza]';
		}
		const encodedBody = encodeURIComponent(emailBody);
		const mailtoLink = `mailto:?subject=${emailSubject}&body=${encodedBody}`;

		console.log('Mailto Link:', mailtoLink); // Debug the URL

		try {
			window.location.href = mailtoLink;
		} catch (error) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Errore Email',
				detail: 'Impossibile aprire il client email. Configura un client email predefinito o copia i dettagli manualmente.',
				key: 'error-toast',
			});
		}
	}

	copyErrorDetails(details: string) {
		navigator.clipboard
			.writeText(details)
			.then(() => {
				this.messageService.add({
					severity: 'success',
					summary: 'Dettagli Copiati',
					detail: "I dettagli dell'errore sono stati copiati negli appunti.",
					key: 'error-toast',
				});
			})
			.catch(() => {
				this.messageService.add({
					severity: 'error',
					summary: 'Errore Copia',
					detail: "Impossibile copiare i dettagli dell'errore.",
					key: 'error-toast',
				});
			});
	}

	private formatErrorDetails(error: any): string {
		let errorResponse: ErrorResponse = error.error;
		let details = '';

		if (errorResponse.error) {
			details += `Errore: ${errorResponse.error}\n\n`;
		}

		if (errorResponse.message) {
			details += `Messaggio: ${errorResponse.message}\n\n`;
		}

		if (errorResponse.rootCause) {
			details += `Root Cause:\n${errorResponse.rootCause}\n\n`;
		}

		if (errorResponse.formattedRootCause) {
			details += `Formatted Root Cause:\n${errorResponse.formattedRootCause}\n\n`;
		}

		if (errorResponse.location) {
			details += `Posizione: ${errorResponse.location}\n\n`;
		}

		if (errorResponse.requestUri) {
			details += `URI: ${errorResponse.requestUri}\n\n`;
		}

		if (errorResponse.httpMethod) {
			details += `Metodo HTTP: ${errorResponse.httpMethod}\n\n`;
		}

		if (errorResponse.timestamp) {
			details += `Timestamp: ${errorResponse.timestamp}\n\n`;
		}

		return details.trim();
	}
}
